import {
  deleteTemplateField,
  downloadMaterialized,
  getAnnotations,
  getFieldCatalog,
  getPreviewHtml,
  getTemplateFields,
  previewWithSampleData,
  renderTag,
  updateAnnotations,
  updateTemplateFields,
  validateTemplateFile,
  type AnnotationInstance,
  type AnnotationsResponse,
  type ContractGenerationTemplateFieldsResponse,
  type ContractGenerationTemplateFieldUpdate,
  type ExtractionResult,
  type FieldCatalog,
  type RenderTagRequest,
  type TemplateAnnotation,
  type TemplateAnnotationWithPath,
} from '@/api';
import { extractErrorDetail } from '@/utils/errorHandler';
import { buildRenderError, RenderError, type SdkLikeResponse } from './renderService';

export interface AnnotationsBundle {
  /** Each tag joined to the schema field it resolves to (`schema_path`). */
  annotations: TemplateAnnotationWithPath[];
  instances: AnnotationInstance[];
}

export const TemplateAuthoringService = {
  async getFieldCatalog(): Promise<FieldCatalog> {
    try {
      const response = await getFieldCatalog();
      if (!response.data) throw new Error('Failed to load field catalog.');
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to load field catalog.'));
    }
  },

  async getPreviewHtml(variantUid: string): Promise<string> {
    try {
      const response = await getPreviewHtml({ path: { variant_uid: variantUid } });
      // Backend returns the raw HTML string as the response body
      if (typeof response.data !== 'string') {
        throw new TypeError('Preview returned an unexpected payload.');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to load template preview.'));
    }
  },

  async getAnnotations(variantUid: string): Promise<AnnotationsBundle> {
    try {
      const response = await getAnnotations({ path: { variant_uid: variantUid } });
      const data = response.data;
      return {
        annotations: data?.annotations ?? [],
        instances: data?.instances ?? [],
      };
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to load annotations.'));
    }
  },

  /**
   * The one save of the field editor: rewrite the .docx from the tag list
   * and, in the same transaction, write the authored field guidance —
   * `fieldMetadata` is keyed by the paths the fields will have AFTER this
   * save. Renamed tags carry their guidance rows along server-side. Failures
   * are typed `RenderError`s (409 while re-validating, 422 for a broken tag or
   * an unknown path) so the page can explain them.
   */
  async saveAnnotations(
    variantUid: string,
    annotations: TemplateAnnotation[],
    fieldMetadata?: ContractGenerationTemplateFieldUpdate[]
  ): Promise<AnnotationsBundle> {
    const response = (await updateAnnotations({
      path: { variant_uid: variantUid },
      body: {
        annotations,
        field_metadata: fieldMetadata && fieldMetadata.length > 0 ? fieldMetadata : undefined,
      },
      throwOnError: false,
    })) as SdkLikeResponse;
    if (response.error) {
      throw buildRenderError(response, 'Failed to save the fields.');
    }
    const data = response.data as AnnotationsResponse | undefined;
    if (!data) {
      throw new RenderError('unknown', 'Failed to save the fields.');
    }
    return {
      annotations: data.annotations,
      instances: data.instances ?? [],
    };
  },

  /**
   * Every field of a variant with whatever an author has written about it, plus
   * the rows whose tag has since been renamed or removed (`orphaned`). Not
   * gated on validation: the panel keeps its rows while the template is being
   * re-extracted, when the schema itself is transiently blank.
   */
  async getFields(variantUid: string): Promise<ContractGenerationTemplateFieldsResponse> {
    try {
      const response = await getTemplateFields({ path: { variant_uid: variantUid } });
      if (!response.data) throw new Error('Failed to load field guidance.');
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to load field guidance.'));
    }
  },

  /**
   * Write authored metadata. Each entry is the field's complete state — an
   * omitted optional clears the stored value, `order_index` is taken as given.
   * The template has to be validated right now: the backend answers 409 while
   * it is still validating and 422 when the last extraction failed, so the
   * failure is a typed `RenderError` the page can explain.
   */
  async saveFields(
    variantUid: string,
    fields: ContractGenerationTemplateFieldUpdate[]
  ): Promise<ContractGenerationTemplateFieldsResponse> {
    const response = (await updateTemplateFields({
      path: { variant_uid: variantUid },
      body: { fields },
      throwOnError: false,
    })) as SdkLikeResponse;
    if (response.error) {
      throw buildRenderError(response, 'Failed to save field guidance.');
    }
    if (!response.data) {
      throw new RenderError('unknown', 'Failed to save field guidance.');
    }
    return response.data as ContractGenerationTemplateFieldsResponse;
  },

  /**
   * Remove an orphaned guidance row — one whose tag left the document for
   * good. Not gated on validation; the backend refuses a path the live schema
   * still owns.
   */
  async deleteField(
    variantUid: string,
    path: string
  ): Promise<ContractGenerationTemplateFieldsResponse> {
    try {
      const response = await deleteTemplateField({
        path: { variant_uid: variantUid, path },
      });
      if (!response.data) throw new Error('Failed to remove the field entry.');
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to remove the field entry.'));
    }
  },

  /**
   * Validate .docx bytes statelessly — used by the Word add-in to check the
   * live (possibly unsaved) document. Returns the same schema + errors the
   * async vault validation task would persist.
   */
  async validateUpload(file: File): Promise<ExtractionResult> {
    try {
      const response = await validateTemplateFile({
        body: { file },
      });
      if (!response.data) throw new Error('Validation returned no result.');
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to validate the document.'));
    }
  },

  /**
   * Render .docx template bytes with backend-generated sample values and
   * return the filled result as a paginated PDF (LibreOffice-converted, so
   * page breaks are exact) — the Word add-in's "how will it look" preview
   * for the live document. Template defaults win where declared; everything
   * else gets a meaningful placeholder.
   */
  async previewSampleUpload(file: File): Promise<Blob> {
    try {
      const response = await previewWithSampleData({
        body: { file },
      });
      if (!response.data) throw new Error('Preview returned no result.');
      if (!(response.data instanceof Blob)) {
        throw new TypeError('Unexpected preview response type.');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to render the sample preview.'));
    }
  },

  /**
   * Render a single tag through the backend so the popover preview matches
   * exactly what the materializer will emit (handles entity binding prefix
   * conventions, filter argument quoting, etc.).
   */
  async renderTag(payload: RenderTagRequest): Promise<string> {
    try {
      const response = await renderTag({ body: payload });
      return response.data?.jinja ?? '';
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to render tag preview.'));
    }
  },

  /**
   * Stream the latest materialized .docx (the current vault file revision).
   * The backend's `PUT /annotations` now auto-materializes on every save, so
   * what you download here always reflects the most recently saved tags.
   */
  async downloadMaterialized(variantUid: string): Promise<Blob> {
    try {
      const response = await downloadMaterialized({
        path: { variant_uid: variantUid },
      });
      if (!response.data) throw new Error('Failed to download materialized template.');
      return response.data as Blob;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to download materialized template.'));
    }
  },
};
